import { useCallback, useEffect, useRef, useState } from 'react';
import { geolocationSource } from './native.js';

/**
 * Location, without the timeout spiral.
 *
 * The previous implementation chained progressively longer timeouts (10s, then
 * 20s, then 30s) and blocked the map until one resolved, which produced a
 * stream of "Timeout expired" errors. Here the map never waits: we ask for a
 * cached position immediately, fall back to a low-accuracy fix, and surface a
 * retry instead of stalling. Denial is a normal state, not an error.
 */
export function useGeolocation({ watch = false } = {}) {
  const geo = useRef(null);
  if (geo.current === null) geo.current = geolocationSource();
  const [position, setPosition] = useState(null);
  const [status, setStatus] = useState('idle');   // idle | locating | ready | denied | unavailable
  const watchId = useRef(null);

  const apply = useCallback((pos) => {
    setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
    setStatus('ready');
  }, []);

  const locate = useCallback(() => {
    const g = geo.current;
    if (!g) { setStatus('unavailable'); return; }
    setStatus('locating');
    g.getCurrentPosition(apply, (err) => {
      if (err.code === err.PERMISSION_DENIED) { setStatus('denied'); return; }
      // Timed out or unavailable: accept anything cached rather than retrying blind.
      g.getCurrentPosition(
        apply,
        () => setStatus('unavailable'),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 600_000 }
      );
    }, { enableHighAccuracy: true, timeout: 6000, maximumAge: 60_000 });
  }, [apply]);

  useEffect(() => { locate(); }, [locate]);

  useEffect(() => {
    const g = geo.current;
    if (!watch || !g || status === 'denied') return;
    watchId.current = g.watchPosition(apply, () => {}, {
      enableHighAccuracy: false, maximumAge: 30_000, timeout: 20_000,
    });
    return () => g.clearWatch(watchId.current);
  }, [watch, status, apply]);

  return { position, status, retry: locate, setManual: (p) => { setPosition(p); setStatus('ready'); } };
}
